import React, { useRef, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';

import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../ui/sonner';
import { StatutBadge } from '../ui/StatutBadge';
import CorrespondanceColonnes from '../fleetImport/CorrespondanceColonnes';
import TableauBrouillon from '../fleetImport/TableauBrouillon';
import RapportImport from '../fleetImport/RapportImport';
import { messageErreur } from '../../lib/erreurs';
import { libelle } from '../../lib/libelles';
import { ETATS_EN_COURS, EXTENSIONS_LISTE, lotImportApi } from '../../lib/lotImportApi';

const FICHIER_VALIDE = /\.(xlsx|csv|pdf)$/i;

const pluriel = (nombre, mot) => `${nombre} ${mot}${nombre > 1 ? 's' : ''}`;

/**
 * Import de la liste des camions d'un lot (Excel, CSV ou PDF).
 *
 * Dépôt -> analyse en arrière-plan (on peut fermer et revenir) -> brouillon à
 * relire (correspondance des colonnes pour un classeur, lignes corrigeables,
 * états prête / à vérifier / refusée / déjà dans le lot) -> confirmation ->
 * rapport. Le travail (`job`) et son suivi appartiennent à l'écran du lot
 * (`useImportListeLot`) : ce dialogue ne fait que l'afficher et l'agir.
 */
export const ImportListeDialog = ({ open, onOpenChange, lotId, reference, job, onJob }) => {
  const champ = useRef(null);
  const [occupe, setOccupe] = useState(false);

  const executer = async (action, message) => {
    setOccupe(true);
    try {
      onJob(await action());
    } catch (erreur) {
      toast.error(messageErreur(erreur, message));
    } finally {
      setOccupe(false);
    }
  };

  const deposer = (evenement) => {
    const fichier = evenement.target.files?.[0];
    evenement.target.value = '';
    if (!fichier) return;
    if (!FICHIER_VALIDE.test(fichier.name)) {
      toast.error('Choisissez un fichier Excel (.xlsx), CSV ou PDF.');
      return;
    }
    executer(() => lotImportApi.upload(lotId, fichier), 'Impossible de lire le fichier');
  };

  const statut = job?.status;
  const enCours = ETATS_EN_COURS.includes(statut);
  const termine = !job || ['done', 'failed', 'cancelled'].includes(statut);
  const camions = job?.summary?.lot_trucks?.included || 0;
  const source = job?.mapping_source ? libelle('import_lot_source', job.mapping_source, '') : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl" data-testid="lot-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {`Importer une liste${reference ? ` · ${reference}` : ''}`}
            {job ? <StatutBadge domaine="import_flotte_travail" code={statut} /> : null}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Déposer la liste des camions du lot, relire le brouillon puis confirmer.
          </DialogDescription>
        </DialogHeader>

        {termine ? (
          <div className="space-y-4">
            {statut === 'failed' ? (
              <div
                role="alert"
                className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm"
                data-testid="lot-import-echec"
              >
                {job.error_message}
              </div>
            ) : null}
            {statut === 'done' ? <RapportImport job={job} /> : null}
            <div className="flex flex-wrap items-center gap-3">
              <input
                ref={champ}
                type="file"
                accept={EXTENSIONS_LISTE}
                className="sr-only"
                onChange={deposer}
                data-testid="lot-import-fichier"
              />
              <Button type="button" onClick={() => champ.current?.click()} disabled={occupe} data-testid="lot-import-choisir">
                {occupe ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Upload className="h-4 w-4" aria-hidden="true" />
                )}
                {job ? 'Nouvelle liste' : 'Choisir une liste (Excel ou PDF)'}
              </Button>
            </div>
          </div>
        ) : null}

        {enCours ? (
          <div
            className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-6"
            role="status"
            data-testid="lot-import-progression"
          >
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
            <span className="font-medium">
              {statut === 'queued' || statut === 'analyzing' ? 'Analyse en cours' : 'Import en cours'}
            </span>
            <span className="text-sm text-muted-foreground">{job.filename}</span>
          </div>
        ) : null}

        {statut === 'draft' ? (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="font-medium">{job.filename}</span>
              {source ? (
                <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs">{source}</span>
              ) : null}
              {(job.warnings || []).map((avertissement) => (
                <span key={avertissement} className="rounded-full border border-flame/30 bg-flame/10 px-2.5 py-0.5 text-xs text-flame-ink">
                  {avertissement}
                </span>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-auto"
                disabled={occupe}
                onClick={() => executer(() => lotImportApi.cancel(job.id), 'Impossible d’annuler l’import')}
                data-testid="lot-import-annuler"
              >
                Annuler l’import
              </Button>
            </div>
            {job.input_kind !== 'pdf' ? (
              <CorrespondanceColonnes
                job={job}
                disabled={occupe}
                onChange={(entrees) =>
                  executer(() => lotImportApi.updateMapping(job.id, entrees), 'Impossible de changer la correspondance')
                }
              />
            ) : null}
            <TableauBrouillon job={job} disabled={occupe} onJob={onJob} />
            <div className="sticky bottom-0 flex justify-end border-t border-border bg-background/95 py-3 backdrop-blur">
              <Button
                type="button"
                size="lg"
                disabled={occupe || camions === 0}
                onClick={() => executer(() => lotImportApi.confirm(job.id, job.draft_version), 'Impossible de lancer l’import')}
                data-testid="lot-import-confirmer"
              >
                {occupe ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                {`Ajouter ${pluriel(camions, 'camion')} au lot`}
              </Button>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="lot-import-fermer">
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportListeDialog;
